// Cycle Trajectory — stacked area chart of the four regime probabilities
// over the trailing N months. Powered by ``GET /api/v1/macro/trajectory``.
//
// Visual contract: the four series stack to ~1.0 every month (the
// backend doesn't strictly normalise but the ensemble's outputs are
// near-stochastic). Colour coding matches MacroCompass so the user
// can pattern-match across widgets without a separate legend lookup.

import { useEffect, useState } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    macroApi,
    type MacroTrajectoryPoint,
} from '../../../api/macro';

interface CycleTrajectoryProps {
    /** Trailing months to fetch. Backend clamps to [1, 240]; default 12. */
    months?: number;
}

const SERIES = [
    { dataKey: 'p_expansion', name: 'Expansion', color: '#10b981' },
    { dataKey: 'p_recovery', name: 'Recovery', color: '#0ea5e9' },
    { dataKey: 'p_slowdown', name: 'Slowdown', color: '#f59e0b' },
    { dataKey: 'p_contraction', name: 'Contraction', color: '#ef4444' },
] as const;

function formatMonthLabel(iso: string): string {
    // ``YYYY-MM-DD`` → ``MMM 'YY``. Avoids Date parsing of an ambiguous
    // string in older Safari by slicing the components directly.
    const [yearStr, monthStr] = iso.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (!year || !month) return iso;
    const monthName = new Date(year, month - 1, 1).toLocaleString(undefined, {
        month: 'short',
    });
    return `${monthName} '${String(year).slice(-2)}`;
}

export const CycleTrajectory = ({ months = 12 }: CycleTrajectoryProps) => {
    const [data, setData] = useState<MacroTrajectoryPoint[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await macroApi.getTrajectory(months);
                if (!cancelled) setData(result);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error ? err.message : 'Failed to load.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [months]);

    if (loading) {
        return (
            <div className="surface-card-skeleton h-80" aria-busy>
                <p className="muted text-sm">Loading regime trajectory…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="surface-card-error">
                <p className="font-bold">Cycle Trajectory unavailable</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="surface-card">
                <h3 className="section-title">Cycle Trajectory</h3>
                <p className="muted text-sm mt-2">
                    No historical regime data yet. The chart will populate
                    once the ARQ worker has produced at least two monthly
                    classifications.
                </p>
            </div>
        );
    }

    const chartData = data.map((row) => ({
        ...row,
        label: formatMonthLabel(row.date),
    }));

    return (
        <div className="surface-card">
            <div className="flex items-baseline justify-between gap-3">
                <div>
                    <h3 className="section-title">Cycle Trajectory</h3>
                    <p className="muted text-xs">
                        Regime probabilities · trailing {data.length} months
                    </p>
                </div>
            </div>

            <div className="mt-4 w-full h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                        <defs>
                            {SERIES.map((s) => (
                                <linearGradient
                                    key={s.dataKey}
                                    id={`grad-${s.dataKey}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor={s.color}
                                        stopOpacity={0.85}
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor={s.color}
                                        stopOpacity={0.4}
                                    />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="currentColor"
                            opacity={0.15}
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            domain={[0, 1]}
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            tickFormatter={(v) => `${Math.round(v * 100)}%`}
                            width={36}
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'rgba(15, 23, 42, 0.97)',
                                border: '1px solid #334155',
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            // Recharts uses each series' fill colour for
                            // ``itemStyle`` text by default, which renders
                            // near-black on the dark contentStyle. Force
                            // both itemStyle + labelStyle to a light slate
                            // so every row stays legible.
                            itemStyle={{ color: '#f8fafc' }}
                            labelStyle={{
                                color: '#cbd5e1',
                                fontWeight: 700,
                                marginBottom: 4,
                            }}
                            formatter={(value: number, name: string) => [
                                `${(value * 100).toFixed(1)}%`,
                                name,
                            ]}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: 12 }}
                            iconType="circle"
                        />
                        {SERIES.map((s) => (
                            <Area
                                key={s.dataKey}
                                type="monotone"
                                dataKey={s.dataKey}
                                name={s.name}
                                stackId="regime"
                                stroke={s.color}
                                fill={`url(#grad-${s.dataKey})`}
                                isAnimationActive
                                animationDuration={400}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default CycleTrajectory;
